import { useEffect, useState } from 'react'
import { api } from '../lib/api'

const QUEST_ICONS: Record<string, string> = {
  DAILY: '⭐',
  WEEKLY: '🗓️',
  STORY: '📖',
  SIDE_QUEST: '🗺️',
  BOSS_BATTLE: '🐉',
  SEASONAL: '🎄',
}

const QUEST_TABS = ['ALL', 'DAILY', 'WEEKLY', 'STORY', 'SIDE_QUEST', 'BOSS_BATTLE']

export default function QuestsPage() {
  const [quests, setQuests] = useState<any[]>([])
  const [achievements, setAchievements] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState('ALL')

  useEffect(() => {
    const type = activeTab === 'ALL' ? undefined : activeTab
    api.getQuests(type).then(({ quests }) => setQuests(quests)).catch(() => {})
  }, [activeTab])

  useEffect(() => {
    api.getAchievements().then(({ achievements }) => setAchievements(achievements)).catch(() => {})
  }, [])

  const unlockedCount = achievements.filter(a => a.unlocked).length

  return (
    <div>
      <div className="page-header">
        <h2>Quests & Achievements</h2>
        <p>Complete quests. Earn XP. Unlock badges. Level up.</p>
      </div>

      {/* Achievement summary */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{unlockedCount}</div>
          <div className="stat-label">Achievements Unlocked</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{achievements.length - unlockedCount}</div>
          <div className="stat-label">Remaining</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{quests.filter(q => q.progress?.completed).length}</div>
          <div className="stat-label">Quests Completed</div>
        </div>
      </div>

      {/* Quest tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {QUEST_TABS.map(tab => (
          <button
            key={tab}
            className={`btn ${activeTab === tab ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'ALL' ? 'All' : `${QUEST_ICONS[tab] || ''} ${tab.replace('_', ' ')}`}
          </button>
        ))}
      </div>

      {/* Quest list */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Active Quests</span>
        </div>
        {quests.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem 0' }}>
            No quests available yet. New quests are coming soon! 🎮
          </p>
        ) : (
          quests.map(q => (
            <div key={q.id} className="quest-item">
              <span className="quest-icon">{QUEST_ICONS[q.questType] || '⭐'}</span>
              <div className="quest-info">
                <div className="quest-name">{q.name}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{q.description}</div>
                {q.progress?.completed && (
                  <span className="badge badge-uncommon" style={{ marginTop: '0.25rem' }}>Completed ✓</span>
                )}
              </div>
              <span className="quest-xp">+{q.xpReward} XP</span>
            </div>
          ))
        )}
      </div>

      {/* Achievements */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <span className="card-title">Achievement Wall</span>
        </div>
        {achievements.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem 0' }}>
            Achievements loading... 🏆
          </p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
            {achievements.map(a => (
              <div key={a.id} style={{
                padding: '1rem',
                background: a.unlocked ? 'rgba(212, 165, 32, 0.08)' : 'rgba(0,0,0,0.2)',
                border: `1px solid ${a.unlocked ? 'rgba(212, 165, 32, 0.3)' : 'rgba(100,100,100,0.2)'}`,
                borderRadius: '4px',
                opacity: a.unlocked ? 1 : 0.5,
              }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                  {a.unlocked ? '🏅' : '🔒'} {a.name}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{a.description}</div>
                <span className={`badge badge-${(a.rarity || 'common').toLowerCase()}`} style={{ marginTop: '0.5rem' }}>
                  {a.rarity} · +{a.xpReward} XP
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
